import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Trash2, Download, Loader2, Inbox, Mail, MapPin, CalendarDays } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useCMS } from "@/hooks/useCMS";

export default function Submissions() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const { reload } = useCMS();

  const load = () => {
    setLoading(true);
    api.get("/submissions").then((r) => setItems(r.data)).catch(() => setItems([])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const approve = async (s) => {
    try {
      await api.post(`/submissions/${s.id}/approve`);
      toast.success("Approved & published as an event");
      load(); reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (s) => {
    if (!window.confirm("Delete this submission?")) return;
    try { await api.delete(`/submissions/${s.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const exportCsv = () => {
    const rows = [["organizer", "email", "event_date", "format", "location", "description", "status", "created_at"]];
    items.forEach((s) => rows.push([s.organizer, s.email, s.event_date, s.format, s.location, (s.description || "").replace(/\n/g, " "), s.status, s.created_at]));
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = "submissions.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const shown = items.filter((s) => filter === "all" || s.status === filter);

  return (
    <div data-testid="admin-submissions">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display font-black uppercase text-3xl">Submissions</h1>
          <p className="mt-1 text-muted-foreground text-sm">Event listing requests from the public site.</p>
        </div>
        <button onClick={exportCsv} className="inline-flex items-center gap-2 rounded-full border border-border bg-card font-bold px-5 py-2.5 hover:border-[#2CACD4] transition-colors cursor-pointer" data-testid="submissions-export">
          <Download size={17} /> Export CSV
        </button>
      </div>

      <div className="mt-6 inline-flex rounded-full bg-muted p-1.5">
        {["all", "new", "approved"].map((f) => (
          <button key={f} onClick={() => setFilter(f)} className={`rounded-full px-4 py-2 text-sm font-bold capitalize transition-colors cursor-pointer ${filter === f ? "bg-[#0F172A] text-white" : "text-foreground/70"}`} data-testid={`submissions-filter-${f}`}>{f}</button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#2CACD4]" /></div>
      ) : shown.length === 0 ? (
        <div className="mt-8 rounded-2xl border border-border bg-card p-12 text-center text-muted-foreground"><Inbox className="mx-auto mb-3" /> No submissions.</div>
      ) : (
        <div className="mt-6 grid gap-4">
          {shown.map((s) => (
            <div key={s.id} className="rounded-2xl border border-border bg-card p-5" data-testid="submission-card">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-3">
                    <h3 className="font-display font-black uppercase text-lg">{s.organizer}</h3>
                    <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${s.status === "approved" ? "bg-[#CAE295]/40 text-green-800" : "bg-[#DB1A5C]/15 text-[#DB1A5C]"}`}>{s.status}</span>
                    <span className="rounded-full bg-muted px-2.5 py-1 text-xs font-semibold capitalize">{s.format}</span>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1.5"><Mail size={14} /> {s.email}</span>
                    <span className="flex items-center gap-1.5"><CalendarDays size={14} /> {s.event_date}</span>
                    {s.location && <span className="flex items-center gap-1.5"><MapPin size={14} /> {s.location}</span>}
                  </div>
                  {s.description && <p className="mt-3 text-sm text-foreground/80">{s.description}</p>}
                </div>
                <div className="flex gap-2">
                  {s.status !== "approved" && (
                    <button onClick={() => approve(s)} className="inline-flex items-center gap-1.5 rounded-full bg-[#CAE295] text-slate-900 font-bold px-4 py-2 hover:brightness-95 transition cursor-pointer" data-testid="submission-approve">
                      <Check size={16} /> Approve
                    </button>
                  )}
                  <button onClick={() => remove(s)} className="rounded-full p-2.5 text-[#DB1A5C] hover:bg-[#DB1A5C]/10 cursor-pointer" aria-label="Delete" data-testid="submission-delete"><Trash2 size={16} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
